/**
 * The verification game: score = evidence the visitor personally verified.
 *
 * Honesty constraint (this site's whole thesis): points are never awarded for reading
 * or scrolling alone - every mission completes on a real action against the real server
 * (a transfer decided, an attack run, a scenario executed, a review verdict recorded)
 * or an explicit visitor acknowledgement (the report-card stamp). The score lives only
 * in this browser's localStorage; the server neither stores nor sees it.
 */

export const GAME_MISSIONS = [
  "try-transfer",
  "try-attack",
  "try-break",
  "try-numbers",
  "try-review",
  "try-receipt",
] as const;

export type MissionId = (typeof GAME_MISSIONS)[number];

export const MISSION_EVENT = "safefds:mission";

const STORAGE_KEY = "safefds-verified-missions-v1";

function isMissionId(value: string): value is MissionId {
  return (GAME_MISSIONS as readonly string[]).includes(value);
}

export function loadCompleted(): Set<MissionId> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is MissionId => typeof v === "string" && isMissionId(v)));
  } catch {
    // Private mode or blocked storage: the game degrades to per-pageview scoring.
    return new Set();
  }
}

/** Marks a mission verified (idempotent) and notifies every score display on the page. */
export function completeMission(id: MissionId): void {
  const done = loadCompleted();
  if (done.has(id)) return;
  done.add(id);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...done]));
  } catch {
    // Storage unavailable: still dispatch so the current pageview updates.
  }
  window.dispatchEvent(new CustomEvent(MISSION_EVENT, { detail: { id } }));
}

export interface Rank {
  icon: string;
  title: string;
}

/** Visitor rank by verified-evidence count (0..6). */
export function rankFor(count: number): Rank {
  if (count >= 6) return { icon: "🏆", title: "FDS 마스터" };
  if (count >= 5) return { icon: "🎖️", title: "수석 심사역" };
  if (count >= 3) return { icon: "🕵️", title: "FDS 심사역" };
  if (count >= 1) return { icon: "🔍", title: "수습 심사역" };
  return { icon: "🙋", title: "방문자" };
}
